import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Tabs, Input, Space, Switch, Spin, Empty } from 'antd';
import LearningCard from '@/components/organisms/Card/LearningCard';
import AcademyExamTable from '@/components/organisms/Table/AcademyExamTable';
import VocabularyTable from '@/components/organisms/Table/VocabularyTable';
import { academyStudentApi } from '@/services/api';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

type Category = 'learning' | 'homework' | 'exam' | 'vocabulary';

interface StudentLearningHistoryProps {
  studentId: string;
}

const StudentLearningHistory: React.FC<StudentLearningHistoryProps> = ({ studentId }) => {
  const [activeCategory, setActiveCategory] = useState<Category>('learning');
  const [learningItems, setLearningItems] = useState<any[]>([]);
  const [examItems, setExamItems] = useState<any[]>([]);
  const [vocabularyItems, setVocabularyItems] = useState<any[]>([]);
  const [searchTerms, setSearchTerms] = useState<Record<Category, string>>({
    learning: '',
    homework: '',
    exam: '',
    vocabulary: '',
  });
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const academyStudentId = useMemo(() => {
    const numeric = Number(studentId);
    return Number.isFinite(numeric) ? numeric : undefined;
  }, [studentId]);
  const activeSearchText = searchTerms[activeCategory];
  const navigate = useNavigate();

  const openScheduleDetail = (scheduleId?: number) => {
    if (!academyStudentId || !scheduleId) return;
    const params = new URLSearchParams();
    params.set('studentId', String(academyStudentId));
    params.set('category', activeCategory);
    navigate(`/admin/student/schedule/${scheduleId}?${params.toString()}`);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initialCategory = params.get('category') as Category | null;
    if (initialCategory && ['learning', 'homework', 'exam', 'vocabulary'].includes(initialCategory)) {
      setActiveCategory(initialCategory);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('category', activeCategory);
    if (!params.get('tab')) params.set('tab', 'learning');
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, [activeCategory]);

  const handleSearchChange = (category: Category, value: string) => {
    setSearchTerms((prev) => ({ ...prev, [category]: value }));
    setPage(1);
  };

  useEffect(() => {
    const fetchCategoryData = async () => {
      if (!academyStudentId) return;
      setLoading(true);
      try {
        const res: any = await academyStudentApi.learningHistory({
          academyStudentId,
          category: activeCategory,
          page: page - 1,
          size: pageSize,
          search: activeSearchText,
          onlyAvailable:
            (activeCategory === 'learning' || activeCategory === 'homework') && showOnlyAvailable
              ? true
              : undefined,
        });
        const data = res?.data || res;
        const content = data?.content || [];
        setTotalElements(Number(data?.totalElements ?? content.length ?? 0));
        if (activeCategory === 'learning' || activeCategory === 'homework') {
          setLearningItems(
            content.map((item: any) => ({
              id: item.id,
              title: item.title,
              units: item.units,
              progress: Number(item.progress ?? 0),
              startDate: item.startDate || '',
              endDate: item.endDate || '-',
              status: item.status as 'completed' | 'in-progress' | 'waiting' | undefined,
              thumbnailUrl: item.thumbnailUrl,
            }))
          );
        } else if (activeCategory === 'exam') {
          setExamItems(
            content.map((item: any) => ({
              id: item.id,
              name: item.name,
              examStatus: item.examStatus,
              examDate: item.examDate,
              score: item.score,
              examResultId: item.examResultId,
            }))
          );
        } else if (activeCategory === 'vocabulary') {
          setVocabularyItems(
            content.map((item: any) => ({
              id: item.id,
              name: item.name,
              wordCount: item.wordCount,
              learningHistory: item.learningHistory,
              registrationDate: item.registrationDate,
            }))
          );
        }
      } catch (error) {
        setTotalElements(0);
        if (activeCategory === 'learning' || activeCategory === 'homework') {
          setLearningItems([]);
        } else if (activeCategory === 'exam') {
          setExamItems([]);
        } else if (activeCategory === 'vocabulary') {
          setVocabularyItems([]);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryData();
  }, [academyStudentId, activeCategory, page, pageSize, activeSearchText, showOnlyAvailable]);

  const renderLearningSection = () => {
    const label = activeCategory === 'homework' ? '숙제' : '학습';
    const avgProgress = learningItems.length
      ? Math.round(learningItems.reduce((sum, item) => sum + Number(item.progress ?? 0), 0) / learningItems.length)
      : 0;
    const allCompleted =
      learningItems.length > 0 &&
      learningItems.every((item) => Number(item.progress ?? 0) >= 100 || item.status === 'completed');
    const anyInProgress = learningItems.some(
      (item) => item.status === 'in-progress' || (Number(item.progress ?? 0) > 0 && Number(item.progress ?? 0) < 100)
    );
    const headerStatus: 'completed' | 'in-progress' | 'waiting' = allCompleted
      ? 'completed'
      : anyInProgress
        ? 'in-progress'
        : 'waiting';

    return (
      <>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="검색어를 입력해주세요."
            allowClear
            value={searchTerms[activeCategory]}
            onChange={(e) => handleSearchChange(activeCategory, e.target.value)}
            style={{ width: 320 }}
          />
          <Space size="small">
            <Switch
              size="small"
              checked={showOnlyAvailable}
              onChange={(checked) => {
                setShowOnlyAvailable(checked);
                setPage(1);
              }}
            />
            <Text>응시 가능한 {label}만 보기</Text>
          </Space>
        </Space>
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Spin />
          </div>
        ) : learningItems.length > 0 ? (
          <LearningCard
            title={`${label} 목록`}
            status={headerStatus}
            progress={avgProgress}
            type={activeCategory === 'homework' ? 'homework' : 'learning'}
            learningItems={learningItems as any}
            onCardClick={openScheduleDetail}
            onItemClick={openScheduleDetail}
          />
        ) : (
          <Empty description={`등록된 ${label} 내역이 없습니다.`} style={{ padding: '40px 0' }} />
        )}
      </>
    );
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: '16px' }}>
        학습이력
      </Title>
      <Tabs activeKey={activeCategory} onChange={(key) => { setActiveCategory(key as Category); setPage(1); }}>
        <TabPane tab="학습" key="learning">
          {renderLearningSection()}
        </TabPane>
        <TabPane tab="숙제" key="homework">
          {renderLearningSection()}
        </TabPane>
        <TabPane tab="시험" key="exam">
          <AcademyExamTable
            data={examItems as any}
            loading={loading}
            currentPage={page}
            pageSize={pageSize}
            totalElements={totalElements}
            onPageChange={(p) => setPage(p)}
            searchText={searchTerms.exam}
            onSearchChange={(value) => handleSearchChange('exam', value)}
            viewMode="admin"
          />
        </TabPane>
        <TabPane tab="단어장" key="vocabulary">
          <VocabularyTable
            data={vocabularyItems as any}
            loading={loading}
            disableNavigation
            currentPage={page}
            pageSize={pageSize}
            totalElements={totalElements}
            onPageChange={(p) => setPage(p)}
            searchText={searchTerms.vocabulary}
            onSearchChange={(value) => handleSearchChange('vocabulary', value)}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default StudentLearningHistory;